<template>
    <div class="viewport">
        <md-list class="md-double-line" style="padding-top: 0">
            <md-divider></md-divider>
            <md-toolbar :md-elevation="1">
                <span class="md-title">Tijdsregistratie</span>
                <span style="margin-left: 10px; color: #777">{{ totalDuration() }}</span>
            </md-toolbar>
            <md-list class="small-height">
                <md-list-item v-for="(registration, index) in workOrderTimeRegistrations" :key="index">
                    <md-avatar>
                        <span class="material-icons"> inventory </span>
                    </md-avatar>
                    <div class="md-list-item-text">
                        <span
                            >{{ formatTime(registration.start_date, true) }} -
                            {{ formatTime(registration.end_date) }}</span
                        >
                        <span>{{ timeDuration(registration) }}</span>
                        <p>{{ registration.description_work }}</p>
                    </div>
                    <md-button class="md-icon-button md-list-action" @click="deleteWorkOrderTimeRegistration(index)">
                        <md-icon>delete</md-icon>
                    </md-button>
                </md-list-item>
                <md-divider v-if="workOrderTimeRegistrations.length > 0"></md-divider>
                <md-button class="md-primary" @click="upsertTimeRegistration()">
                    <md-icon>add</md-icon>
                    werktijd
                </md-button>
            </md-list>
        </md-list>

        <md-dialog
            style="z-index: 9"
            :md-active.sync="showDialogUpsertTimeRegistration"
            v-if="showDialogUpsertTimeRegistration"
        >
            <md-dialog-title>Nieuwe tijdsregistratie</md-dialog-title>

            <md-dialog-content>
                <md-datepicker v-model="registrationDate">
                    <label>Datum</label>
                </md-datepicker>

                <div class="md-layout md-gutter">
                    <div class="md-layout-item md-layout md-gutter">
                        <div class="md-layout-item" style="padding-top: 5px">
                            <label for="time-registration-van">Van:</label>
                        </div>
                        <div class="md-layout-item">
                            <vue-timepicker
                                hour-label="uur"
                                minute-label="min"
                                manual-input
                                v-model="timeFrom"
                                minuteInterval="15"
                            ></vue-timepicker>
                        </div>
                    </div>
                </div>
                <div class="md-layout md-gutter">
                    <div class="md-layout-item md-layout md-gutter">
                        <div class="md-layout-item" style="padding-top: 5px">
                            <label for="time-registration-van">Tot:</label>
                        </div>
                        <div class="md-layout-item">
                            <vue-timepicker
                                hour-label="uur"
                                minute-label="min"
                                manual-input
                                v-model="timeTo"
                                minuteInterval="15"
                            ></vue-timepicker>
                        </div>
                    </div>
                </div>
                <md-field style="margin-top: 20px">
                    <label>Beschrijving werk</label>
                    <md-textarea v-model="description"></md-textarea>
                </md-field>
            </md-dialog-content>

            <md-dialog-actions>
                <md-button @click="cancelUpsertWorkOrderTimeRegistration()" class="md-primary">Cancel</md-button>
                <md-button @click="upsertAddWorkOrderTimeRegistration()" class="md-primary">Save</md-button>
            </md-dialog-actions>
        </md-dialog>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { IWorkOrderTimeRegistrationEntity } from '@phrospr/lib-models';
import * as toastr from 'toastr';
import VueTimepicker from 'vue2-timepicker';
import moment from 'moment';

@Component({ components: { VueTimepicker } })
export default class WorkOrderInfo extends Vue {
    @Prop() private workOrderTimeRegistrations: IWorkOrderTimeRegistrationEntity[];

    showDialogUpsertTimeRegistration = false;

    timeFrom: string;
    timeTo: string;
    description: string = '';
    registrationDate: Date;

    constructor() {
        super();
        this.load();
    }

    private async load() {}

    totalDuration() {
        let total = 0;
        this.workOrderTimeRegistrations.forEach(
            registration =>
                (total += (registration.end_date?.getTime() ?? 0) - (registration.start_date?.getTime() ?? 0)),
        );
        if (total === 0) return '';
        return moment.utc(total).format('HH[u] mm[min]');
    }

    parseTimePickerValue(input: string | { HH: string; mm: string }) {
        if (typeof input === 'string') return input;
        return `${input.HH ?? '00'}:${input.mm ?? '00'}`;
    }

    formatTime(input: Date, withDate = false) {
        const format = `${withDate ? 'DD/MM/YYYY ' : ''}HH:mm`;
        return moment(input).format(format);
    }

    timeDuration(registration: IWorkOrderTimeRegistrationEntity): string {
        if (!registration?.end_date || !registration?.start_date) return '';
        return moment.utc(registration.end_date.getTime() - registration.start_date.getTime()).format('HH[u] mm[min]');
    }

    public async upsertAddWorkOrderTimeRegistration() {
        if (!this.registrationDate || !this.timeFrom || !this.timeTo) {
            toastr.error('Geen van tijdstip & tot tijdstip gemarkeerd');
            return;
        }
        const dateFormatted = moment(this.registrationDate).format('YYYY-MM-DD');
        const timeFrom = moment(`${dateFormatted} ${this.parseTimePickerValue(this.timeFrom)}`, 'YYYY-MM-DD HH:mm');
        const timeTo = moment(`${dateFormatted} ${this.parseTimePickerValue(this.timeTo)}`, 'YYYY-MM-DD HH:mm');
        if (timeFrom.valueOf() > timeTo.valueOf()) {
            toastr.error('Eind tijd moet na begin tijd komen!');
            return;
        }

        this.showDialogUpsertTimeRegistration = false;
        this.workOrderTimeRegistrations.push({
            start_date: timeFrom.toDate(),
            end_date: timeTo.toDate(),
            description_work: this.description,
        });
        // Sort the array by start_date ascending
        this.workOrderTimeRegistrations.sort((a, b) => a.start_date.getTime() - b.start_date.getTime());

        this.$forceUpdate();
    }

    deleteWorkOrderTimeRegistration(index: number) {
        this.workOrderTimeRegistrations.splice(index, 1);
        this.workOrderTimeRegistrations.sort((a, b) => a.start_date.getTime() - b.start_date.getTime());
        this.$forceUpdate();
    }

    public resetFields() {
        const remainder = 15 - (moment().minute() % 15);
        const defaultTime = moment().add(remainder, 'minutes').format('HH:mm');
        this.timeFrom = defaultTime;
        this.timeTo = defaultTime;
        this.description = '';
        this.registrationDate = new Date();
    }

    public cancelUpsertWorkOrderTimeRegistration() {
        this.showDialogUpsertTimeRegistration = false;
    }

    public upsertTimeRegistration() {
        this.resetFields();
        this.showDialogUpsertTimeRegistration = true;
    }
}
</script>

<style lang="scss" scoped>
.md-list.md-double-line {
    min-height: 40px !important;
}

.md-list-item-content {
    min-height: 40px !important;
}

.md-list {
    min-height: 40px !important;
}

.md-double-line {
    min-height: 40px !important;
}

.md-triple-linet {
    min-height: 40px !important;
}
.md-ripple {
    min-height: 40px !important;
}
.md-disabled {
    min-height: 40px !important;
}

.small-height {
    min-height: 40px !important;
}
</style>
